import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $applyNodeReplacement,
  $getNodeByKey,
  $isTextNode,
  DecoratorNode,
  DOMExportOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";
import React, { useCallback } from "react";

import { getBlockTypeIcon } from "./BlockAutocompleteDropdown";

export type SerializedBlockMentionNode = Spread<
  {
    blockId: string;
    blockTitle: string;
    blockType: string;
  },
  SerializedLexicalNode
>;

export class BlockMentionNode extends DecoratorNode<React.ReactElement> {
  __blockId: string;
  __blockTitle: string;
  __blockType: string;

  static getType(): string {
    return "block-mention";
  }

  static clone(node: BlockMentionNode): BlockMentionNode {
    return new BlockMentionNode(node.__blockId, node.__blockTitle, node.__blockType, node.__key);
  }

  constructor(blockId: string, blockTitle: string, blockType: string, key?: NodeKey) {
    super(key);
    this.__blockId = blockId;
    this.__blockTitle = blockTitle;
    this.__blockType = blockType;
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.style.display = "inline";
    return span;
  }

  updateDOM(): false {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("span");
    element.setAttribute("data-block-id", this.__blockId);
    element.textContent = `@${this.__blockTitle}`;
    return { element };
  }

  static importJSON(serializedNode: SerializedBlockMentionNode): BlockMentionNode {
    return $createBlockMentionNode(
      serializedNode.blockId,
      serializedNode.blockTitle,
      serializedNode.blockType
    );
  }

  exportJSON(): SerializedBlockMentionNode {
    return {
      ...super.exportJSON(),
      type: "block-mention",
      blockId: this.__blockId,
      blockTitle: this.__blockTitle,
      blockType: this.__blockType,
    };
  }

  getTextContent(): string {
    return `@${this.__blockTitle}`;
  }

  isInline(): boolean {
    return true;
  }

  decorate(): React.ReactElement {
    return (
      <BlockMentionChip
        nodeKey={this.__key}
        blockTitle={this.__blockTitle}
        blockType={this.__blockType}
      />
    );
  }
}

const BlockMentionChip: React.FC<{
  nodeKey: string;
  blockTitle: string;
  blockType: string;
}> = ({ nodeKey, blockTitle, blockType }) => {
  const [editor] = useLexicalComposerContext();

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (!node) {
          return;
        }

        const prev = node.getPreviousSibling();
        const next = node.getNextSibling();
        node.remove();

        const prevIsText = $isTextNode(prev);
        const nextIsText = $isTextNode(next);
        const prevEndsSpace = prevIsText && prev.getTextContent().endsWith(" ");
        const nextStartsSpace = nextIsText && next.getTextContent().startsWith(" ");

        if (prevEndsSpace && nextStartsSpace) {
          // Both sides have a space — remove one to keep a single space
          next.setTextContent(next.getTextContent().slice(1));
        } else if (prevEndsSpace && !nextIsText) {
          // Chip was at the end — trim trailing space
          prev.setTextContent(prev.getTextContent().slice(0, -1));
        } else if (nextStartsSpace && !prevIsText) {
          // Chip was at the start — trim leading space
          next.setTextContent(next.getTextContent().slice(1));
        }
      });
    },
    [editor, nodeKey]
  );

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-gray-100 rounded-full text-sm align-middle cursor-default mx-0.5"
      contentEditable={false}
      suppressContentEditableWarning
    >
      <span className="material-icons text-blue-600" style={{ fontSize: "14px" }}>
        {getBlockTypeIcon(blockType)}
      </span>
      <span className="text-gray-900 truncate max-w-[160px] font-medium" title={blockTitle}>
        {blockTitle}
      </span>
      <button
        type="button"
        className="text-gray-400 hover:text-gray-600 flex items-center cursor-pointer"
        onMouseDown={handleRemove}
        title="Remove"
      >
        <span className="material-icons" style={{ fontSize: "14px" }}>
          close
        </span>
      </button>
    </span>
  );
};

export function $createBlockMentionNode(
  blockId: string,
  blockTitle: string,
  blockType: string
): BlockMentionNode {
  return $applyNodeReplacement(new BlockMentionNode(blockId, blockTitle, blockType));
}

export function $isBlockMentionNode(
  node: LexicalNode | null | undefined
): node is BlockMentionNode {
  return node instanceof BlockMentionNode;
}
